import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fullGroupName, type StructureData, type Ou, type Group, type User } from '../types.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import { apiClient } from '@/lib/api.ts'
import { StructureTreeExplorer } from '../explorer/StructureTreeExplorer.tsx'
import { buildTree, type DefaultParents, type ExplorerNode, type TreeBuildItem } from '../explorer/types.ts'

interface Props {
  data: StructureData
  update: (patch: Partial<StructureData>) => void
  embedded?: boolean
}

const SYSTEMHAUS_OU_NAME = '_IMP'

interface StandardItem {
  id: string
  type: string
  name_template: string
  description?: string | null
  config?: {
    groupType?: string
    ou?: string
    parent?: string
    groupIds?: string[]
  } | null
}

export function AdExplorerTab({ data, update }: Props) {
  const a = useAnonymize()

  const { data: itemsData } = useQuery<{ items: StandardItem[] }>({
    queryKey: ['it-standard-items'],
    queryFn: () => apiClient.get('/it-structures/global/items').then((r) => r.data),
  })

  const impOuId = useMemo(
    () =>
      data.ous.find(
        (o) => o.parent === null && o.name.trim().toLowerCase() === SYSTEMHAUS_OU_NAME.toLowerCase(),
      )?.id ?? null,
    [data.ous],
  )

  const prefixRootId = useMemo(() => {
    const p = (data.domainPrefix ?? '').trim().toLowerCase()
    if (!p) return null
    return (
      data.ous.find((o) => o.parent === null && o.name.trim().toLowerCase() === p)?.id ?? null
    )
  }, [data.ous, data.domainPrefix])

  /**
   * Ersetzt den `{PREFIX}`-Platzhalter in Standard-Item-Namen durch das Kunden-
   * Kuerzel. Alles in den globalen Standards wird ueber diesen Mechanismus
   * auf den konkreten Kunden projiziert.
   */
  const resolvePrefix = useMemo(() => {
    const p = (data.domainPrefix ?? '').trim()
    return (s: string) => s.replace(/\{PREFIX\}/gi, p || '{PREFIX}')
  }, [data.domainPrefix])

  /**
   * Globale Standard-OUs aus `/global/items` als virtuelle read-only OUs.
   * Parent-Aufloesung ueber `config.parent` (Name-Lookup gegen Customer-OUs
   * und bereits gebaute Virtual-OUs), mit `{PREFIX}`-Substitution.
   * - OUs, deren aufgeloester Name bereits als Customer-OU existiert, werden
   *   uebersprungen (Kunde gewinnt).
   * - Orphans (Parent nicht aufloesbar) fallen als Fallback unter die Kunden-
   *   Root, damit sie sichtbar bleiben.
   */
  const virtualStandardOus = useMemo<Ou[]>(() => {
    const stdOus = (itemsData?.items ?? []).filter((i) => i.type === 'ou')
    if (stdOus.length === 0) return []

    const nameToOuId = new Map<string, string>()
    for (const ou of data.ous) nameToOuId.set(ou.name.trim().toLowerCase(), ou.id)

    // 1. Synthetic IDs reservieren fuer alle Std-OUs, die noch nicht als Customer-OU existieren
    const virtualIdByName = new Map<string, string>()
    for (const std of stdOus) {
      const name = resolvePrefix(std.name_template).trim()
      const key = name.toLowerCase()
      if (!name || nameToOuId.has(key) || virtualIdByName.has(key)) continue
      virtualIdByName.set(key, `__std__${std.id}`)
    }

    // 2. Virtual-OUs bauen, Parent aufloesen
    const result: Ou[] = []
    const seen = new Set<string>()
    for (const std of stdOus) {
      const name = resolvePrefix(std.name_template).trim()
      const key = name.toLowerCase()
      if (!name || nameToOuId.has(key) || seen.has(key)) continue
      const id = virtualIdByName.get(key)
      if (!id) continue
      seen.add(key)

      let parentId: string | null = null
      const rawParent = std.config?.parent?.trim()
      if (rawParent) {
        const parentKey = resolvePrefix(rawParent).toLowerCase()
        parentId = nameToOuId.get(parentKey) ?? virtualIdByName.get(parentKey) ?? prefixRootId
      }
      result.push({
        id,
        name,
        parent: parentId,
        source: 'global',
        kind: 'regular',
        description: std.description ?? undefined,
      })
    }
    return result
  }, [itemsData, data.ous, resolvePrefix, prefixRootId])

  /**
   * Kombinierte OU-Liste (Customer + Virtual-Global) + Name→ID-Lookup.
   * Wird fuer Gruppen/User-Zuordnung und fuer den Tree-Build genutzt.
   */
  const mergedOus = useMemo(() => [...data.ous, ...virtualStandardOus], [data.ous, virtualStandardOus])
  const ouIdByName = useMemo(() => {
    const m = new Map<string, string>()
    for (const o of mergedOus) m.set(o.name.trim().toLowerCase(), o.id)
    return m
  }, [mergedOus])

  /**
   * Globale Standard-Gruppen aus `/global/items` als virtuelle read-only
   * Gruppen-Nodes. Ziel-OU per `config.ou` (mit `{PREFIX}`-Substitution),
   * Fallback auf `_IMP`. Im Konflikt (gleicher Name bereits in data.groups)
   * gewinnt der Customer-Eintrag.
   */
  const virtualStandardGroups = useMemo<Group[]>(() => {
    const templates = (itemsData?.items ?? []).filter((i) => i.type === 'group')
    if (templates.length === 0) return []
    const existingNames = new Set(data.groups.map((g) => g.name.trim().toLowerCase()))
    return templates
      .map<Group | null>((t) => {
        const name = resolvePrefix(t.name_template).trim()
        if (!name || existingNames.has(name.toLowerCase())) return null
        const rawOu = t.config?.ou?.trim()
        const resolvedOuId = rawOu ? ouIdByName.get(resolvePrefix(rawOu).toLowerCase()) : null
        const ouId = resolvedOuId ?? impOuId ?? prefixRootId ?? undefined
        if (!ouId) return null
        return {
          id: `__std__${t.id}`,
          name,
          type: (t.config?.groupType as Group['type']) ?? 'FU',
          description: t.description ?? undefined,
          ouId,
          source: 'global',
        }
      })
      .filter((g): g is Group => g !== null)
  }, [itemsData, data.groups, ouIdByName, impOuId, prefixRootId, resolvePrefix])

  /**
   * Globale Standard-User als virtuelle read-only User. Ziel-OU per
   * `config.ou` (mit `{PREFIX}`-Substitution). Ohne aufloesbare OU kein
   * Rendering — die globale Konfiguration muss das Ziel sauber setzen.
   */
  const virtualStandardUsers = useMemo<User[]>(() => {
    const templates = (itemsData?.items ?? []).filter((i) => i.type === 'user')
    if (templates.length === 0) return []
    const existingNames = new Set(data.users.map((u) => u.username.trim().toLowerCase()))
    return templates
      .map<User | null>((t) => {
        const name = resolvePrefix(t.name_template).trim()
        if (!name || existingNames.has(name.toLowerCase())) return null
        const rawOu = t.config?.ou?.trim()
        const ouId = rawOu ? ouIdByName.get(resolvePrefix(rawOu).toLowerCase()) : null
        if (!ouId) return null
        return {
          id: `__std__${t.id}`,
          username: name,
          description: t.description ?? undefined,
          groupIds: [],
          ouId,
          source: 'global',
        }
      })
      .filter((u): u is User => u !== null)
  }, [itemsData, data.users, ouIdByName, resolvePrefix])

  const treeItems = useMemo<TreeBuildItem[]>(() => {
    const allGroups = [...data.groups, ...virtualStandardGroups]
    const allUsers = [...data.users, ...virtualStandardUsers]
    return [
      ...mergedOus.map<TreeBuildItem>((ou) => ({
        id: ou.id,
        kind: 'ou',
        name: ou.name,
        description: ou.description,
        parentId: ou.parent,
        ouKind: ou.kind,
        isReadOnly: ou.source === 'global',
      })),
      ...allGroups.map<TreeBuildItem>((g) => ({
        id: g.id,
        kind: 'group',
        name: a.token(fullGroupName(data.domainPrefix, g.type, g.name)),
        description: g.description,
        ouId: g.ouId,
        groupType: g.type,
        isReadOnly: g.source === 'global',
      })),
      ...allUsers.map<TreeBuildItem>((u) => ({
        id: u.id,
        kind: 'user',
        name: a.token(u.username),
        description: u.description,
        ouId: u.ouId,
        isReadOnly: u.source === 'global',
      })),
    ]
  }, [mergedOus, data.groups, virtualStandardGroups, data.users, virtualStandardUsers, data.domainPrefix, a])

  const nodes = useMemo(
    () => buildTree(treeItems, data.defaultParents ?? {}),
    [treeItems, data.defaultParents],
  )

  // ─── Action Callbacks ──────────────────────────────────────────────────────

  function handleAddOu(parentId: string | null) {
    const name = window.prompt('Name der neuen OU:')?.trim()
    if (!name) return
    const ou: Ou = {
      id: crypto.randomUUID(),
      name,
      parent: parentId,
      source: 'custom',
      kind: 'regular',
    }
    update({ ous: [...data.ous, ou] })
  }

  function handleRename(node: ExplorerNode) {
    const name = window.prompt('Neuer Name:', node.name)?.trim()
    if (!name || name === node.name) return
    update({ ous: data.ous.map((o) => (o.id === node.id ? { ...o, name } : o)) })
  }

  function handleDelete(node: ExplorerNode) {
    if (node.kind === 'ou') {
      const toDelete = new Set<string>()
      const collect = (id: string) => {
        toDelete.add(id)
        data.ous.filter((o) => o.parent === id).forEach((c) => collect(c.id))
      }
      collect(node.id)
      update({
        ous: data.ous.filter((o) => !toDelete.has(o.id)),
        groups: data.groups.filter((g) => !g.ouId || !toDelete.has(g.ouId)),
        users: data.users.filter((u) => !u.ouId || !toDelete.has(u.ouId)),
      })
    } else if (node.kind === 'group') {
      update({
        groups: data.groups.filter((g) => g.id !== node.id),
        users: data.users.map((u) => ({
          ...u,
          groupIds: u.groupIds.filter((gid) => gid !== node.id),
        })),
        permissions: data.permissions.filter((p) => p.groupId !== node.id),
      })
    } else if (node.kind === 'user') {
      update({ users: data.users.filter((u) => u.id !== node.id) })
    }
  }

  function handleToggleDefault(ouId: string, target: keyof DefaultParents) {
    const current = data.defaultParents?.[target]
    const next: DefaultParents = { ...(data.defaultParents ?? {}) }
    if (current === ouId) {
      delete next[target]
    } else {
      next[target] = ouId
    }
    // Leeres Objekt → undefined (sauberes JSON)
    update({ defaultParents: Object.keys(next).length > 0 ? next : undefined })
  }

  return (
    <StructureTreeExplorer
      nodes={nodes}
      domainLabel={data.domain || 'Domain'}
      domainSubLabel="Domain-Root"
      title="AD-Explorer"
      ouCount={data.ous.length}
      groupCount={data.groups.length}
      userCount={data.users.length}
      emptyMessage='Noch keine OUs vorhanden. Nutze den „OUs"-Tab um die Kunden-Root anzulegen.'
      hint="Rechtsklick für Optionen · globale Items read-only"
      actions={{
        onAddOu: handleAddOu,
        onRename: handleRename,
        renameableKinds: ['ou'],
        onDelete: handleDelete,
        defaultParents: data.defaultParents,
        onToggleDefault: handleToggleDefault,
      }}
    />
  )
}
